import { supabase } from '../config/supabase';
import { Conversation, CreateConversationInput } from '../types';
import { analyticsService } from './analytics';

/**
 * Conversations Service
 * Manages buyer-seller conversation CRUD operations with Supabase
 */
export const conversationsService = {
  /**
   * Create a new conversation between buyer and seller for a specific item
   * @param itemId - The ID of the item being discussed
   * @param sellerId - The ID of the seller
   * @returns Promise<Conversation> The created conversation
   * @throws Error if user is not authenticated or creation fails
   *
   * Note: Database enforces UNIQUE constraint on (item_id, buyer_id)
   * to prevent duplicate conversations for the same buyer-item pair
   */
  async createConversation(itemId: string, sellerId: string): Promise<Conversation> {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      throw new Error('User not authenticated');
    }

    // Buyer creates conversation, buyer_id is current user
    const { data, error } = await supabase
      .from('conversations')
      .insert({
        item_id: itemId,
        buyer_id: user.id,
        seller_id: sellerId,
      })
      .select(`
        *,
        item:clothing_items(*),
        buyer:profiles!conversations_buyer_id_fkey(*),
        seller:profiles!conversations_seller_id_fkey(*)
      `)
      .single();

    if (error) {
      // Handle duplicate conversation gracefully
      if (error.code === '23505') { // Unique violation
        throw new Error('You already have a conversation for this item');
      }
      throw error;
    }

    // Conversations are unique per (item, buyer): this is a new buyer showing interest
    analyticsService.trackChatStarted();

    return data;
  },

  /**
   * Get all conversations for the current user (as buyer or seller)
   * @param userId - The ID of the user (optional, defaults to current user)
   * @returns Promise<Conversation[]> Array of conversations with joined data
   * @throws Error if query fails
   */
  async getConversations(userId?: string): Promise<Conversation[]> {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      throw new Error('User not authenticated');
    }

    const targetUserId = userId || user.id;

    const { data, error } = await supabase
      .from('conversations')
      .select(`
        *,
        item:clothing_items(*),
        buyer:profiles!conversations_buyer_id_fkey(*),
        seller:profiles!conversations_seller_id_fkey(*)
      `)
      .or(`buyer_id.eq.${targetUserId},seller_id.eq.${targetUserId}`)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return data || [];
  },

  /**
   * Get a single conversation by ID
   * @param id - The conversation ID
   * @returns Promise<Conversation> The conversation with joined data
   * @throws Error if conversation not found or user doesn't have access
   */
  async getConversationById(id: string): Promise<Conversation> {
    const { data, error } = await supabase
      .from('conversations')
      .select(`
        *,
        item:clothing_items(*),
        buyer:profiles!conversations_buyer_id_fkey(*),
        seller:profiles!conversations_seller_id_fkey(*)
      `)
      .eq('id', id)
      .single();

    if (error) throw error;

    return data;
  },

  /**
   * Get or create a conversation for a specific item
   * Useful pattern for "Start Conversation" button - creates if doesn't exist
   * @param itemId - The ID of the item
   * @param sellerId - The ID of the seller
   * @returns Promise<Conversation> Existing or newly created conversation
   * @throws Error if query fails
   */
  async getOrCreateConversation(itemId: string, sellerId: string): Promise<Conversation> {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      throw new Error('User not authenticated');
    }

    // First, try to get existing conversation
    const { data: existingConversation, error: selectError } = await supabase
      .from('conversations')
      .select(`
        *,
        item:clothing_items(*),
        buyer:profiles!conversations_buyer_id_fkey(*),
        seller:profiles!conversations_seller_id_fkey(*)
      `)
      .eq('item_id', itemId)
      .eq('buyer_id', user.id)
      .maybeSingle();

    if (selectError) throw selectError;

    if (existingConversation) {
      return existingConversation;
    }

    // If not found, create new conversation
    return this.createConversation(itemId, sellerId);
  },

  /**
   * Get conversation for a specific item (if exists)
   * More efficient than getConversations() when you only need to check one item
   * @param itemId - The ID of the item
   * @returns Promise<Conversation | null> Existing conversation or null
   * @throws Error if query fails
   */
  async getConversationForItem(itemId: string): Promise<Conversation | null> {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase
      .from('conversations')
      .select(`
        *,
        item:clothing_items(*),
        buyer:profiles!conversations_buyer_id_fkey(*),
        seller:profiles!conversations_seller_id_fkey(*)
      `)
      .eq('item_id', itemId)
      .eq('buyer_id', user.id)
      .maybeSingle();

    if (error) throw error;

    return data;
  },

  /**
   * Get all conversations about a specific item where the current user is the seller
   * Used by the "Verkocht aan" flow to list potential buyers for an item
   * @param itemId - The ID of the item
   * @returns Promise<Conversation[]> Conversations with joined buyer profiles
   * @throws Error if not authenticated or query fails
   */
  async getConversationsForItemAsSeller(itemId: string): Promise<Conversation[]> {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase
      .from('conversations')
      .select(`
        *,
        item:clothing_items(*),
        buyer:profiles!conversations_buyer_id_fkey(*),
        seller:profiles!conversations_seller_id_fkey(*)
      `)
      .eq('item_id', itemId)
      .eq('seller_id', user.id)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return data || [];
  },

  /**
   * Get all conversations with enriched metadata (unread count, last message)
   * Optimized version that minimizes database requests
   * @param userId - The ID of the user (optional, defaults to current user)
   * @returns Promise<Conversation[]> Array of conversations with metadata
   * @throws Error if query fails
   */
  async getConversationsWithMetadata(userId?: string): Promise<any[]> {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      throw new Error('User not authenticated');
    }

    const targetUserId = userId || user.id;

    // Fetch conversations with their messages in a single query
    const { data: conversations, error: convsError } = await supabase
      .from('conversations')
      .select(`
        *,
        item:clothing_items(*),
        buyer:profiles!conversations_buyer_id_fkey(*),
        seller:profiles!conversations_seller_id_fkey(*),
        messages!inner(
          id,
          content,
          created_at,
          read_at,
          sender_id,
          message_type,
          metadata
        )
      `)
      .or(`buyer_id.eq.${targetUserId},seller_id.eq.${targetUserId}`)
      .order('created_at', { foreignTable: 'messages', ascending: false });

    if (convsError) throw convsError;

    // Process conversations to extract metadata
    const enrichedConversations = (conversations || []).map((conv: any) => {
      const messages = conv.messages || [];
      
      // Calculate unread count (messages not from current user that haven't been read)
      const unreadCount = messages.filter((m: any) => 
        m.sender_id !== targetUserId && !m.read_at
      ).length;

      // Get last message
      const lastMessage = messages.length > 0 ? messages[0] : null;

      return {
        ...conv,
        messages: undefined, // Remove messages array to match original type
        unreadCount,
        lastMessagePreview: lastMessage?.content || '',
        lastMessageTime: lastMessage?.created_at || conv.created_at,
        lastMessageType: lastMessage?.message_type || 'text',
        lastMessageMetadata: lastMessage?.metadata || null,
      };
    });

    // Sort by last message time (most recent first)
    enrichedConversations.sort((a: any, b: any) =>
      new Date(b.lastMessageTime || 0).getTime() - new Date(a.lastMessageTime || 0).getTime()
    );

    return enrichedConversations;
  },

  /**
   * Archive a conversation
   * Sets status to 'archived' and records archived_at timestamp
   * @param id - The conversation ID
   * @returns Promise<void>
   * @throws Error if update fails or user doesn't have access
   */
  async archiveConversation(id: string): Promise<void> {
    const { error } = await supabase
      .from('conversations')
      .update({
        status: 'archived',
        archived_at: new Date().toISOString(),
      })
      .eq('id', id);

    if (error) throw error;
  },

  /**
   * Mark conversation as completed
   * Used when transaction is finalized
   * @param id - The conversation ID
   * @returns Promise<void>
   * @throws Error if update fails
   */
  async completeConversation(id: string): Promise<void> {
    const { error } = await supabase
      .from('conversations')
      .update({
        status: 'completed',
        archived_at: new Date().toISOString(),
      })
      .eq('id', id);

    if (error) throw error;
  },

  /**
   * Reactivate an archived conversation
   * Sets status back to 'active' and clears archived_at
   * @param id - The conversation ID
   * @returns Promise<void>
   * @throws Error if update fails
   */
  async reactivateConversation(id: string): Promise<void> {
    const { error } = await supabase
      .from('conversations')
      .update({
        status: 'active',
        archived_at: null,
      })
      .eq('id', id);

    if (error) throw error;
  },

  /**
   * Delete a conversation
   * Permanently removes a conversation from the database
   * Use case: Deleting empty conversations when user closes chat without sending messages
   * @param id - The conversation ID
   * @returns Promise<void>
   * @throws Error if delete fails or user doesn't have access
   */
  async deleteConversation(id: string): Promise<void> {
    const { error } = await supabase
      .from('conversations')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },
};
